import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const cache = new Map();
const inflight = new Map();
const stale = new Set();
const LS = "avter-snap:";

// Requests started by the inline script in index.html (before the JS bundle loads).
const takeEarly = (path) => {
  const early = window.__avterEarly?.[path];
  if (!early) return null;
  delete window.__avterEarly[path];
  return early;
};

const load = (path) => {
  if (!inflight.has(path)) {
    const req = takeEarly(path) || api.get(path).then((res) => res.data);
    inflight.set(path, req.then((data) => {
      cache.set(path, data);
      if (stale.delete(path)) {
        try { window.localStorage.setItem(LS + path, JSON.stringify(data)); } catch { /* storage unavailable */ }
      }
      return data;
    }).finally(() => inflight.delete(path)));
  }
  return inflight.get(path);
};

// Start requests early (before React renders) to shorten the data waterfall.
export const prefetch = (paths) => paths.forEach((p) => (!cache.has(p) || stale.has(p)) && load(p).catch(() => {}));

// First-paint data: last visit's copy (localStorage) or the bundled snapshot; revalidated by load().
export const seed = (snapshot) => Object.entries(snapshot).forEach(([path, bundled]) => {
  let data = bundled;
  try { data = JSON.parse(window.localStorage.getItem(LS + path)) || bundled; } catch { /* use bundled */ }
  if (!cache.has(path)) { cache.set(path, data); stale.add(path); }
});

export function useApi(path) {
  const [state, setState] = useState(() =>
    cache.has(path) ? { data: cache.get(path), loading: false, error: null } : { data: null, loading: true, error: null },
  );

  useEffect(() => {
    if (!path) return undefined;
    let active = true;
    if (cache.has(path)) {
      const current = cache.get(path);
      setState((s) => (s.data === current ? s : { data: current, loading: false, error: null }));
      if (!stale.has(path)) return undefined;
      load(path).then((data) => {
        if (active && JSON.stringify(data) !== JSON.stringify(current)) setState({ data, loading: false, error: null });
      }).catch(() => {});
      return () => { active = false; };
    }
    setState({ data: null, loading: true, error: null });
    load(path)
      .then((data) => active && setState({ data, loading: false, error: null }))
      .catch((error) => active && setState({ data: null, loading: false, error }));
    return () => {
      active = false;
    };
  }, [path]);

  return state;
}
